import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, User, BookOpen, Play, Video } from "lucide-react";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import BibleVerse from "@/components/BibleVerse";
import { translateScriptureReference } from "@/utils/bibleTranslations";
import { parseDateAsLocal } from "@/lib/utils";

interface Sermon {
  id: string;
  title: string;
  description: string | null;
  sermon_date: string;
  speaker: string;
  audio_url: string | null;
  video_url: string | null;
  scripture_reference: string | null;
}

interface SermonTranslation {
  title: string;
  description: string | null;
  scripture_reference: string | null;
  speaker: string;
}

const Sermons = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [sermons, setSermons] = useState<Sermon[]>([]);
  const [translations, setTranslations] = useState<Record<string, SermonTranslation>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSermons();

    // Subscribe to real-time updates for sermons
    const sermonsChannel = supabase
      .channel('sermons-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sermons'
        },
        () => {
          fetchSermons();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sermons_translations'
        },
        () => {
          fetchSermons();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(sermonsChannel);
    };
  }, [language]);

  const fetchSermons = async () => {
    try {
      const { data: sermonsData, error: sermonsError } = await supabase
        .from("sermons")
        .select("*")
        .order("sermon_date", { ascending: false });

      if (sermonsError) throw sermonsError;
      setSermons(sermonsData || []);

      // Fetch translations for the current language
      if (sermonsData && sermonsData.length > 0) {
        const { data: translationsData, error: translationsError } = await supabase
          .from("sermons_translations")
          .select("sermon_id, title, description, scripture_reference, speaker")
          .eq("language", language)
          .in("sermon_id", sermonsData.map(s => s.id));

        if (!translationsError) {
          const translationsMap: Record<string, SermonTranslation> = {};
          if (translationsData && translationsData.length > 0) {
            translationsData.forEach(t => {
              translationsMap[t.sermon_id] = {
                title: t.title,
                description: t.description,
                scripture_reference: t.scripture_reference,
                speaker: t.speaker
              };
            });
          }
          setTranslations(translationsMap);
        }
      } else {
        setTranslations({});
      }
    } catch (error: any) {
      toast({
        title: t("sermons.error.title"),
        description: t("sermons.error.description"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getSermonContent = (sermon: Sermon) => {
    const translation = translations[sermon.id];
    const scriptureRef = translation?.scripture_reference || sermon.scripture_reference;
    console.log('🎤 Sermon content:', {
      sermonId: sermon.id,
      currentLanguage: language,
      hasTranslation: !!translation,
      translationTitle: translation?.title,
      originalTitle: sermon.title,
      usingTitle: translation?.title || sermon.title,
      translationScriptureRef: translation?.scripture_reference,
      originalScriptureRef: sermon.scripture_reference,
      usingScriptureRef: scriptureRef,
      allTranslations: Object.keys(translations)
    });
    return {
      title: translation?.title || sermon.title,
      description: translation?.description || sermon.description,
      scripture_reference: scriptureRef,
      speaker: translation?.speaker || sermon.speaker
    };
  };

  const parseScriptureReferences = (reference: string | null): string[] => {
    if (!reference) return [];
    // Split by comma, semicolon, or newline, trim whitespace, and filter empty strings
    const refs = reference.split(/[,;\n]/).map(ref => ref.trim()).filter(ref => ref.length > 0);
    // Translate each reference to the current language
    return refs.map(ref => translateScriptureReference(ref, language));
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container mx-auto px-4 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">{t("sermons.title")}</h1>
            <p className="text-lg text-muted-foreground">
              {t("sermons.subtitle")}
            </p>
          </div>

          {loading ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardHeader>
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : sermons.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">{t("sermons.nosermons")}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {sermons.map((sermon) => {
                const content = getSermonContent(sermon);
                return (
                  <Card 
                    key={sermon.id} 
                    className="flex flex-col cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => navigate(`/sermons/${sermon.id}`)}
                  >
                    <CardHeader>
                      <CardTitle className="line-clamp-2">{content.title}</CardTitle>
                      <CardDescription className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {format(parseDateAsLocal(sermon.sermon_date), "MMMM d, yyyy")}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col gap-4">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4" />
                        <span>{content.speaker}</span>
                      </div>
                      
                      {content.scripture_reference && (
                        <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                          {parseScriptureReferences(content.scripture_reference).map((ref, idx) => (
                            <BibleVerse key={idx} reference={ref} compact />
                          ))}
                        </div>
                      )}

                      {content.description && (
                        <p className="text-sm text-muted-foreground line-clamp-3">
                          {content.description}
                        </p>
                      )}

                    <div className="mt-auto flex gap-2">
                      {sermon.audio_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(sermon.audio_url!, "_blank");
                          }}
                        >
                          <Play className="h-4 w-4 mr-2" />
                          {t("sermons.audio")}
                        </Button>
                      )}
                      {sermon.video_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(sermon.video_url!, "_blank");
                          }}
                        >
                          <Video className="h-4 w-4 mr-2" />
                          {t("sermons.video")}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Sermons;
